import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api, { mediaUrl } from '../api/client';
import Seo from '../components/Seo';
import Breadcrumb from '../components/common/Breadcrumb';
import ProductCard from '../components/product/ProductCard';
import Pagination from '../components/common/Pagination';
import Spinner from '../components/common/Spinner';
import { useSettings } from '../context/SettingsContext';
import { IconGrid } from '../components/common/Icons';

const SORTS = [
  ['best-selling', 'Best selling'],
  ['created-desc', 'Date, new to old'],
  ['created-asc', 'Date, old to new'],
  ['price-asc', 'Price, low to high'],
  ['price-desc', 'Price, high to low'],
  ['title-asc', 'Alphabetically, A-Z'],
  ['title-desc', 'Alphabetically, Z-A'],
];

export default function Collection({ search = false }) {
  const { slug = 'all' } = useParams();
  const [params, setParams] = useSearchParams();
  const { settings } = useSettings();
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [result, setResult] = useState({ items: [], pagination: { page: 1, pages: 1, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem('eg_view') || 'grid');

  const page = Number(params.get('page') || 1);
  const sort = params.get('sort') || 'best-selling';
  const q = params.get('q') || '';
  const onSale = params.get('sale') === '1';
  const minPrice = params.get('min') || '';
  const maxPrice = params.get('max') || '';

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    if (search || slug === 'all') {
      setCategory(null);
      return;
    }
    api.get(`/categories/slug/${slug}`).then((r) => setCategory(r.category)).catch(() => setCategory(null));
  }, [slug, search]);

  useEffect(() => {
    setLoading(true);
    const p = { page, sort, limit: settings.shop?.productsPerPage || 24, q: q || undefined, onSale: onSale || undefined, minPrice: minPrice || undefined, maxPrice: maxPrice || undefined };
    if (!search && slug !== 'all') p.category = slug;
    api
      .get('/products', { params: p })
      .then((r) => setResult(r))
      .catch(() => setResult({ items: [], pagination: { page: 1, pages: 1, total: 0 } }))
      .finally(() => setLoading(false));
  }, [slug, page, sort, q, onSale, minPrice, maxPrice, search, settings.shop?.productsPerPage]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === '' || v == null) next.delete(k);
    else next.set(k, v);
    if (k !== 'page') next.delete('page');
    setParams(next);
  };

  const title = search ? (q ? `Search results for “${q}”` : 'Search') : category ? category.name : settings.pages?.collectionTitle || 'Products';
  const subCats = useMemo(() => (category ? categories.filter((c) => c.parent && c.parent._id === category._id) : categories.filter((c) => c.showInMenu && !c.parent)), [categories, category]);
  const crumbs = search ? [{ label: 'Search' }] : category ? [...(category.parent ? [{ label: category.parent.name, url: `/collections/${category.parent.slug}` }] : []), { label: category.name }] : [{ label: 'Products' }];

  return (
    <>
      <Seo title={title} description={category?.description || (search ? `Search products for ${q}` : settings.seo?.defaultDescription)} seo={category?.seo || {}} image={category?.image} breadcrumbs={[{ label: 'Home', url: '/' }, ...crumbs]} noIndex={search} />
      <div className="page-header sf__collection-header py-8 md:py-12 text-center" style={category?.banner ? { backgroundImage: `url(${mediaUrl(category.banner)})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
        <div className="container-fluid">
          <h1 className="page-header__title text-3xl md:text-4xl">{title}</h1>
          {category?.description && <div className="rte text-color-secondary mt-3 max-w-2xl mx-auto" dangerouslySetInnerHTML={{ __html: category.description }} />}
        </div>
      </div>
      <Breadcrumb items={crumbs} />
      <section className="sf__featured-collection" style={{ backgroundColor: '#fafafa', '--column-gap': '30px', '--column-gap-mobile': '10px', '--row-gap': '40px' }}>
        <div className="container-fluid section-py py-10">
          {subCats.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {subCats.map((c) => (
                <Link key={c._id} to={`/collections/${c.slug}`} className="sf__btn sf__btn-secondary sf__btn-small bg-white">{c.name}{c.productCount ? <span className="text-color-secondary ml-1">({c.productCount})</span> : null}</Link>
              ))}
            </div>
          )}
          <div className="sf__collection-toolbar flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="text-sm text-color-secondary">{result.pagination.total} products</div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center text-sm cursor-pointer"><input type="checkbox" className="mr-2" checked={onSale} onChange={(e) => setParam('sale', e.target.checked ? '1' : '')} />On sale</label>
              <div className="flex items-center gap-1 text-sm">
                <input type="number" placeholder="Min" value={minPrice} onChange={(e) => setParam('min', e.target.value)} className="form-control w-20 py-1 bg-white" style={{ lineHeight: '1.5rem' }} />
                <span>–</span>
                <input type="number" placeholder="Max" value={maxPrice} onChange={(e) => setParam('max', e.target.value)} className="form-control w-20 py-1 bg-white" style={{ lineHeight: '1.5rem' }} />
              </div>
              <div className="sf__custom-select sf__select-plain">
                <select value={sort} onChange={(e) => setParam('sort', e.target.value)} className="form-control bg-white py-1" style={{ lineHeight: '1.5rem' }} aria-label="Sort by">
                  {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="hidden md:flex items-center gap-1">
                <button type="button" className={`p-2 rounded ${view === 'grid' ? 'bg-black text-white' : 'bg-white'}`} onClick={() => { setView('grid'); localStorage.setItem('eg_view', 'grid'); }} aria-label="Grid view"><IconGrid width={16} height={16} /></button>
                <button type="button" className={`p-2 rounded ${view === 'list' ? 'bg-black text-white' : 'bg-white'}`} onClick={() => { setView('list'); localStorage.setItem('eg_view', 'list'); }} aria-label="List view"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 6h16M4 12h16M4 18h16" /></svg></button>
              </div>
            </div>
          </div>
          {loading ? (
            <Spinner />
          ) : result.items.length === 0 ? (
            <div className="text-center py-20 text-color-secondary">
              <p className="text-lg mb-4">No products found{q ? ` for “${q}”` : ''}.</p>
              <Link to="/collections/all" className="sf__btn sf__btn-primary">Browse all products</Link>
            </div>
          ) : (
            <div className="sf__ms">
              <div className={`sf-grid ${view === 'list' ? 'eg-list-view' : 'grid grid-cols-2 sf__col-4 md:grid-cols-3 xl:grid-cols-4'}`} style={{ gap: 'var(--row-gap) var(--column-gap)' }}>
                {result.items.map((p) => (
                  <div className="sf-column" key={p._id}><ProductCard product={p} listView={view === 'list'} /></div>
                ))}
              </div>
            </div>
          )}
          <Pagination page={result.pagination.page} pages={result.pagination.pages} onChange={(n) => setParam('page', String(n))} />
        </div>
      </section>
    </>
  );
}
